import { c as _c } from "react/compiler-runtime";
import { usePortfolios } from "../api/queries/usePortfolios";
import { PortfolioBusinessType, GetRealEstateManagementSchemaType, PortfolioBusinessSchemaType, UnitBusinessType } from "../api/schemas/schemas";
import { ModalTypeEnum, useModal } from "@/components/providers/modal-provider/ModalProvider";
import { AssociateListingToUnitModal } from "../components/associate-listing-to-unit-modal/AssociateListingToUnitModal";
import { useTranslations } from "next-intl";
import { useBackofficeListingContext } from "@/components/modules/admin/hooks/useBackofficeListingContext";
import { useAdminUserPortfolios } from "@/components/modules/admin/api/queries/useAdminUserPortfolios";
import { useAdminUpdateListingResourceId } from "@/components/modules/admin/api/mutations/useAdminUpdateListingResourceId";
import { useUpdateListingResourceId } from "@/components/modules/listings/api/mutations/useUpdateListingResourceId";
import { useIsMobile } from "@/hooks/use-mobile";
export type PortfolioRoomsResult = {
  portfolios: GetRealEstateManagementSchemaType[];
  totalRooms: number;
};

/**
 * Pure: filter portfolios down to those with a Rooms business that has
 * at least one Room unit, and return the total Room-unit count.
 *
 * Coerces enum compare via .toString() because the BE returns
 * `businessType`/`unitType` as integers while the FE enum is string.
 */
export const getPortfolioRoomBusinesses = (portfoliosData: GetRealEstateManagementSchemaType[] | undefined): PortfolioRoomsResult => {
  if (!portfoliosData) return {
    portfolios: [],
    totalRooms: 0
  };
  const portfolios: GetRealEstateManagementSchemaType[] = portfoliosData.map(portfolio => {
    const businesses = portfolio.businesses || [];
    const roomBusinesses: PortfolioBusinessSchemaType[] = businesses.filter(b => {
      if (b.businessType.toString() !== PortfolioBusinessType.Rooms) return false;
      const units = b.units || [];
      return units.some(u => u.unitType.toString() === UnitBusinessType.Room);
    });
    if (roomBusinesses.length === 0) return null;
    return {
      ...portfolio,
      businesses: roomBusinesses
    } as GetRealEstateManagementSchemaType;
  }).filter(Boolean) as GetRealEstateManagementSchemaType[];
  const totalRooms = portfolios.reduce((acc, p) => {
    const biz = p.businesses || [];
    const roomsInPortfolio = biz.reduce((bAcc, b) => {
      const units = b.units || [];
      return bAcc + units.filter(u => u.unitType.toString() === UnitBusinessType.Room).length;
    }, 0);
    return acc + roomsInPortfolio;
  }, 0);
  return {
    portfolios,
    totalRooms
  };
};
export const usePortfolioUtils = () => {
  const $ = _c(18);
  const {
    openModal
  } = useModal();
  const isMobile = useIsMobile();
  useTranslations("modules.portfolio.associateListingToUnit");
  const {
    isBackofficeView,
    userId: backofficeUserId
  } = useBackofficeListingContext();
  const {
    data: ownPortfoliosData
  } = usePortfolios();
  const {
    data: adminUserPortfoliosData
  } = useAdminUserPortfolios(backofficeUserId ?? "");
  const adminUpdateMutation = useAdminUpdateListingResourceId();
  const regularUpdateMutation = useUpdateListingResourceId();
  let t0;
  if ($[0] !== adminUserPortfoliosData?.data || $[1] !== isBackofficeView || $[2] !== ownPortfoliosData?.data) {
    t0 = () => getPortfolioRoomBusinesses(isBackofficeView ? adminUserPortfoliosData?.data : ownPortfoliosData?.data);
    $[0] = adminUserPortfoliosData?.data;
    $[1] = isBackofficeView;
    $[2] = ownPortfoliosData?.data;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const handleGetPortfolioRoomBusinesses = t0;
  const rawPortfoliosCount = isBackofficeView ? adminUserPortfoliosData?.data?.length ?? 0 : ownPortfoliosData?.data?.length ?? 0;
  const hasAnyPortfolio = rawPortfoliosCount > 0;
  let t1;
  if ($[4] !== handleGetPortfolioRoomBusinesses || $[5] !== isMobile || $[6] !== openModal) {
    t1 = t2 => {
      const {
        listingId,
        currentUnitId,
        onSuccess
      } = t2;
      const resource = handleGetPortfolioRoomBusinesses();
      const children = <AssociateListingToUnitModal listingId={listingId} resource={resource} currentUnitId={currentUnitId} onSuccess={onSuccess} />;
      isMobile ? openModal(ModalTypeEnum.GENERIC_SHEET, {
        children,
        side: "bottom",
        className: "p-0",
        hasClose: false
      }) : openModal(ModalTypeEnum.GENERIC_DIALOG, {
        children,
        childrenClassName: "p-0"
      });
    };
    $[4] = handleGetPortfolioRoomBusinesses;
    $[5] = isMobile;
    $[6] = openModal;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const handleAddListingToUnit = t1;
  let t2;
  if ($[8] !== adminUpdateMutation || $[9] !== isBackofficeView || $[10] !== regularUpdateMutation) {
    t2 = t3 => {
      const {
        listingId: listingId_0,
        resourceId,
        unitName,
        onSuccess: onSuccess_0
      } = t3;
      const mutation = isBackofficeView ? adminUpdateMutation : regularUpdateMutation;
      mutation.mutate({
        listingId: listingId_0,
        resourceId,
        unitName
      }, {
        onSuccess: onSuccess_0
      });
    };
    $[8] = adminUpdateMutation;
    $[9] = isBackofficeView;
    $[10] = regularUpdateMutation;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const handleUpdateListingResource = t2;
  const isUpdatingResource = isBackofficeView ? adminUpdateMutation.isPending : regularUpdateMutation.isPending;
  let t3;
  if ($[12] !== handleAddListingToUnit || $[13] !== handleGetPortfolioRoomBusinesses || $[14] !== handleUpdateListingResource || $[15] !== hasAnyPortfolio || $[16] !== isUpdatingResource) {
    t3 = {
      handleGetPortfolioRoomBusinesses,
      handleAddListingToUnit,
      handleUpdateListingResource,
      isUpdatingResource,
      hasAnyPortfolio
    };
    $[12] = handleAddListingToUnit;
    $[13] = handleGetPortfolioRoomBusinesses;
    $[14] = handleUpdateListingResource;
    $[15] = hasAnyPortfolio;
    $[16] = isUpdatingResource;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  return t3;
};